// Helpers for deriving the LLM-facing names of MCP connection tools.
//
// The backend namespaces every remote tool as "{connection_slug}_{tool_name}"
// (truncated to 64 chars) so it can never collide with a built-in handler.
// Skills reference MCP tools by that derived name, so the frontend must
// compute it identically.

import { McpServer, isMcpBinding } from '../../integrations/mcpServerRegistry';

const MAX_LLM_TOOL_NAME_LEN = 64;

// MUST stay in sync with
// backend/api/services/mcp_client_service.py::connection_slug.
export function connectionSlug(name: string): string {
  const slug = (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug.slice(0, 24) || 'mcp';
}

export function mcpToolLlmName(
  connection: { slug?: string; name: string },
  toolName: string
): string {
  return `${connection.slug || connectionSlug(connection.name)}_${toolName}`.slice(
    0,
    MAX_LLM_TOOL_NAME_LEN
  );
}

export interface McpSkillToolOption {
  name: string;
  label: string;
  description: string;
}

/**
 * Enumerate the grantable MCP tools of an agent's connections, mirroring the
 * backend's iter_agent_mcp_tools after binding resolution: enabled
 * bindings whose org server is enabled, filtered to enabled_tools (absent =
 * all discovered), deduped by LLM name. Bindings carry no catalog of their
 * own — `servers` (GET /mcp-servers) supplies it. Legacy inline entries
 * (url + tools on the entry itself) still resolve from their own copy.
 */
export function mcpSkillToolOptions(
  actions: Record<string, any>,
  servers: McpServer[] = []
): McpSkillToolOption[] {
  const connections: any[] = Array.isArray(actions?.mcp) ? actions.mcp : [];
  const serversById = new Map(servers.map((server) => [server.id, server]));
  const options: McpSkillToolOption[] = [];
  const seen = new Set<string>();

  for (const entry of connections) {
    if (!entry || entry.enabled === false) continue;

    let name: string;
    let slug: string | undefined;
    let tools: any[];
    if (isMcpBinding(entry)) {
      const server = serversById.get(entry.mcp_server_id);
      if (!server || !server.enabled) continue;
      name = server.name;
      slug = entry.slug;
      tools = Array.isArray(server.tools) ? server.tools : [];
    } else {
      name = entry.name;
      slug = entry.slug;
      tools = Array.isArray(entry.tools) ? entry.tools : [];
    }
    if (tools.length === 0) continue;
    const enabled: string[] = entry.enabled_tools ?? tools.map((tool: any) => tool?.name);

    for (const tool of tools) {
      const toolName = tool?.name;
      if (typeof toolName !== 'string' || !toolName.trim()) continue;
      if (!enabled.includes(toolName)) continue;
      const llmName = mcpToolLlmName({ slug, name }, toolName);
      if (seen.has(llmName)) continue;
      seen.add(llmName);
      options.push({
        name: llmName,
        label: `${name} · ${toolName}`,
        description: tool.description || '',
      });
    }
  }

  return options;
}
